import AddCircleIcon from "@mui/icons-material/AddCircle";
import HomeIcon from "@mui/icons-material/Home";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Box,
  Button,
  Container,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { BottomSheetHeader, PageTitleBar } from "common/components";
import { AddFeedEventDialog } from "common/components/AddNewEventDialogs";
import DetailViewLoading from "common/components/DetailViewLoading";
import { useTranslation } from "common/locales";
import { FieldsList } from "features/farmOverview";
import AnimalGroupDetailDialog from "features/feedBudgetPlan/AnimalGroupDetailDialog";
import type { StockClassMonthEntry } from "features/feedBudgetPlan/types";
import { upsertGrazingMonthGrowthRate } from "features/feedBudgetPlan/upsertGrazingMonthGrowthRate";
import { useActualFeedData } from "features/feedBudgetPlan/useActualFeedData";
import { useFeedBudgetCalculations } from "features/feedBudgetPlan/useFeedBudgetCalculations";
import { useFeedBudgetPlan } from "features/feedBudgetPlan/useFeedBudgetPlan";
import { GrassWedgeDialog } from "features/grassWedge";
import {
  getAllFieldsForAnimalGroupIdFromArray,
  getAnimalGroupColorFromArray,
  getAvailableDryMatterForAnimalGroupIdFromArray,
  getFieldGeomsForAnimalGroupFromArray,
  getTotalAnimalCountFromArray,
  getTotalLivestockUnitsFromArray,
  useAnimalGroup,
  useCurrentFarm,
  useCurrentFarmAnimalGroups,
  useFields,
  useStockClassesByIds,
} from "model";
import { LIVESTOCK_UNIT_CONVERSION } from "model/animalGroupTypes";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Link,
  useMatch,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useUIStore } from "stores";
import { analytics, AnalyticsEvents } from "../../common/analytics";
import AnimalGroupMenu from "./AnimalGroupMenu";
import AssignFieldDialog from "./AssignFieldDialog";
import SetCurrentFieldDialog from "./SetCurrentFieldDialog";

import { RotationTimelineDialog } from "./RotationTimeline";

const BELLFLOWER_COLOR = "#D3C9E7";
const PLANNING_CARD_HEIGHT = 112;

interface MetricItemProps {
  label: string;
  value: string | number;
  unit?: string;
  secondaryValue?: string;
  warning?: boolean;
  infoTooltip?: string;
}

function MetricItem({
  label,
  value,
  unit,
  secondaryValue,
  warning,
  infoTooltip,
}: MetricItemProps) {
  return (
    <Box
      sx={{
        py: 0.75,
        borderBottom: "1px solid #F0F0F0",
        "&:last-child": {
          borderBottom: "none",
        },
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", display: "block", fontSize: "0.7rem" }}
        >
          {label}
        </Typography>
        {infoTooltip && (
          <Tooltip title={infoTooltip} arrow placement="top">
            <InfoOutlinedIcon
              sx={{
                fontSize: 14,
                color: "text.secondary",
                cursor: "help",
                opacity: 0.6,
                "&:hover": { opacity: 1 },
              }}
            />
          </Tooltip>
        )}
      </Box>
      <Box
        sx={{ display: "flex", alignItems: "baseline", gap: 0.5, minWidth: 0 }}
      >
        <Typography
          variant="body1"
          sx={{
            fontWeight: 600,
            color: warning ? "#DC2626" : "#1a1a2e",
            fontStyle: warning ? "italic" : "normal",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {value}
        </Typography>
        {unit && (
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", fontSize: "0.7rem", flexShrink: 0 }}
          >
            {unit}
          </Typography>
        )}
        {secondaryValue && (
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", fontSize: "0.65rem", flexShrink: 0 }}
          >
            ({secondaryValue})
          </Typography>
        )}
      </Box>
    </Box>
  );
}

interface WarningItemProps {
  label: string;
  value?: string;
  action?: React.ReactNode;
}

function WarningItem({ label, value, action }: WarningItemProps) {
  return (
    <Box
      sx={{
        py: 0.75,
        px: 1,
        mx: -1,
        borderBottom: "1px solid #F7E880",
        backgroundColor: "#FDFBEB",
        borderRadius: 1,
      }}
    >
      <Typography
        variant="caption"
        sx={{ color: "#A48137", display: "block", fontSize: "0.7rem" }}
      >
        {label}
      </Typography>
      {value && (
        <Typography variant="body1" sx={{ fontWeight: 600, color: "#A48137" }}>
          {value}
        </Typography>
      )}
      {action}
    </Box>
  );
}

interface MetricsCardProps {
  children: React.ReactNode;
}

function MetricsCard({ children }: MetricsCardProps) {
  return (
    <Box
      sx={{
        backgroundColor: "white",
        borderRadius: 2,
        px: 1.5,
        py: 0.5,
        border: `1px solid ${BELLFLOWER_COLOR}`,
      }}
    >
      {children}
    </Box>
  );
}

interface StockClassRowProps {
  name: string;
  count: number;
}

function StockClassRow({ name, count }: StockClassRowProps) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        py: 0.5,
        borderBottom: "1px dotted #E0E0E0",
        "&:last-child": {
          borderBottom: "none",
        },
      }}
    >
      <Typography
        variant="body1"
        sx={{ fontWeight: 600, color: "#1a1a2e", flexShrink: 0 }}
      >
        {count}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          color: "text.secondary",
          fontSize: "0.7rem",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          flex: 1,
          minWidth: 0,
        }}
      >
        {name}
      </Typography>
    </Box>
  );
}

export default function AnimalGroupDetail() {
  const [assignFieldDialogOpen, setAssignFieldDialogOpen] =
    useState<boolean>(false);
  const [setCurrentFieldDialogOpen, setSetCurrentFieldDialogOpen] =
    useState<boolean>(false);
  const [feedBudgetDialogOpen, setFeedBudgetDialogOpen] = useState(false);
  const [feedEventDialogOpen, setFeedEventDialogOpen] = useState(false);
  const { animalGroupId } = useParams<{ animalGroupId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  // Auto-open assign field dialog when coming from onboarding
  useEffect(() => {
    if (searchParams.get("openAssignField") === "true") {
      setAssignFieldDialogOpen(true);
      // Clear the param so refreshing doesn't reopen the dialog
      searchParams.delete("openAssignField");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const { data: currentFarm } = useCurrentFarm();

  // Use proper React Query hook for animal group data (fetches if not in cache)
  const { data: animalGroup } = useAnimalGroup(animalGroupId);

  // Get stock classes for this animal group
  const { data: stockClasses = [] } = useStockClassesByIds(
    animalGroup?.stockClasses
  );

  // Backend fallback values for animal count and livestock units
  const backendAnimalCount = useMemo(
    () => getTotalAnimalCountFromArray(stockClasses),
    [stockClasses]
  );
  const backendLivestockUnits = useMemo(
    () => getTotalLivestockUnitsFromArray(stockClasses),
    [stockClasses]
  );

  const handleBackToFarm = () => {
    if (currentFarm?.guid) {
      navigate(`/farm/${currentFarm.guid}`);
    }
  };

  const {
    plan,
    createPlan,
    updatePlan,
    updatePlanMetadata,
    addStockClassToGroup,
    removeStockClassFromGroup,
    addSupplementFeedType,
    removeSupplementFeedType,
    isDirty,
    isSaving,
    savePlan,
    discardGroupChanges,
  } = useFeedBudgetPlan();
  const feedBudgetComputed = useFeedBudgetCalculations(plan);
  const actualFeedData = useActualFeedData();

  const grassWedgeMatch = useMatch("/animal-group/:animalGroupId/grass-wedge");
  const rotationTimelineMatch = useMatch(
    "/animal-group/:animalGroupId/rotations"
  );
  const { t } = useTranslation();

  // Use React Query for fields data
  const { data: allFields = [] } = useFields();
  const fields = useMemo(
    () => getAllFieldsForAnimalGroupIdFromArray(allFields, animalGroupId),
    [allFields, animalGroupId]
  );

  // Get all animal groups to compute color by index
  const { data: allAnimalGroups = [] } = useCurrentFarmAnimalGroups();

  // Get animal group color for visual accent
  const animalGroupColor = useMemo(
    () =>
      animalGroup?.color ||
      getAnimalGroupColorFromArray(allAnimalGroups, animalGroupId),
    [allAnimalGroups, animalGroupId, animalGroup?.color]
  );

  const dryMatterAvailable = useMemo(
    () => getAvailableDryMatterForAnimalGroupIdFromArray(fields, animalGroup),
    [fields, animalGroup]
  );

  const totalAreaForGroup = fields.reduce((acc, f) => acc + f.area, 0);

  const showGrassWedgeButton = true; // always allow to open grasswedge

  useZoomToFieldGeoms(animalGroupId);

  const openMoveOutEventDialog = useUIStore(
    (state) => state.openMoveOutEventDialog
  );

  if (!animalGroup) return <DetailViewLoading size={2} />;

  const { name } = animalGroup;

  // Calculate metrics
  const fieldCount = fields.length;
  const totalAreaHa = totalAreaForGroup / 10000; // Convert m² to Ha
  const totalAreaRounded = Math.round(totalAreaHa * 10) / 10;

  // Find current field (where animals are grazing now)
  const currentField = fields.find((f) => f.animalGroupNow === animalGroupId);
  const handleMoveToShed = () => {
    if (!currentField) return;
    openMoveOutEventDialog({
      open: true,
      currentFieldWithAnimals: currentField,
      moveToShed: true,
    });
  };

  // Convert dry matter to tonnes
  const dryMatterTonnes = (dryMatterAvailable / 1000).toFixed(1);

  const selectedBudget = plan?.animalGroupBudgets.find(
    (agb) => agb.animalGroupId === animalGroupId
  );

  // Derive all metrics from feed budget plan's current month when available
  const currentMonth = new Date().getMonth();
  const currentGroupRow = feedBudgetComputed?.animalGroupRows.get(
    animalGroupId ?? ""
  )?.[currentMonth];

  const animalCount = useMemo(() => {
    if (!selectedBudget) return backendAnimalCount;
    return selectedBudget.stockClassPlans.reduce(
      (sum, sc) => sum + (sc.months[currentMonth]?.animalCount ?? 0),
      0
    );
  }, [selectedBudget, backendAnimalCount, currentMonth]);

  const livestockUnits = useMemo(() => {
    if (!selectedBudget) return backendLivestockUnits;
    return selectedBudget.stockClassPlans.reduce((sum, sc) => {
      const monthData = sc.months[currentMonth];
      const rate = sc.defaultStockClassName
        ? LIVESTOCK_UNIT_CONVERSION[sc.defaultStockClassName] ?? 0.5
        : 0.5;
      return sum + (monthData?.animalCount ?? 0) * rate;
    }, 0);
  }, [selectedBudget, backendLivestockUnits, currentMonth]);

  const totalDailyDemand =
    currentGroupRow?.totalDemandKgDay ??
    (stockClasses
      ? stockClasses.reduce(
          (sum, sc) => sum + sc.consumptionKgPerDay * sc.animalCount,
          0
        )
      : 0);

  // Calculate days of forage available (grazing days ahead)
  const grazingDaysAhead =
    totalDailyDemand > 0
      ? Math.round(dryMatterAvailable / totalDailyDemand)
      : 0;

  // Calculate stocking rate (LU/Ha)
  const stockingRate =
    totalAreaHa > 0 ? (livestockUnits / totalAreaHa).toFixed(1) : "-";

  const handleOpenFeedBudgetDialog = () => {
    if (!plan) {
      createPlan();
    }
    setFeedBudgetDialogOpen(true);
  };

  const handleUpdateStockClass = useCallback(
    (
      targetAnimalGroupId: string,
      stockClassId: string,
      month: number,
      yearOffset: 0 | 1,
      field: keyof Pick<
        StockClassMonthEntry,
        "animalCount" | "averageWeightKg" | "allocationPercent"
      >,
      value: number
    ) => {
      updatePlan((p) => ({
        ...p,
        animalGroupBudgets: p.animalGroupBudgets.map((agb) =>
          agb.animalGroupId === targetAnimalGroupId
            ? {
                ...agb,
                stockClassPlans: agb.stockClassPlans.map((sc) =>
                  sc.stockClassId === stockClassId
                    ? {
                        ...sc,
                        months: sc.months.map((m) =>
                          m.month === month &&
                          (m.yearOffset ?? 0) === yearOffset
                            ? { ...m, [field]: value }
                            : m
                        ),
                      }
                    : sc
                ),
              }
            : agb
        ),
      }));
    },
    [updatePlan]
  );

  const handleUpdateSupplementAmount = useCallback(
    (
      targetAnimalGroupId: string,
      month: number,
      feedTypeId: string,
      value: number
    ) => {
      updatePlan((p) => ({
        ...p,
        animalGroupBudgets: p.animalGroupBudgets.map((agb) =>
          agb.animalGroupId === targetAnimalGroupId
            ? {
                ...agb,
                supplementPlans: agb.supplementPlans.map((s) =>
                  s.month === month
                    ? { ...s, amounts: { ...s.amounts, [feedTypeId]: value } }
                    : s
                ),
              }
            : agb
        ),
      }));
    },
    [updatePlan]
  );

  const handleUpdateSupplementUtilisation = useCallback(
    (targetAnimalGroupId: string, utilisationPercent: number) => {
      updatePlan((p) => ({
        ...p,
        animalGroupBudgets: p.animalGroupBudgets.map((agb) =>
          agb.animalGroupId === targetAnimalGroupId
            ? {
                ...agb,
                supplementPlans: agb.supplementPlans.map((s) => ({
                  ...s,
                  utilisationPercent,
                })),
              }
            : agb
        ),
      }));
    },
    [updatePlan]
  );

  const handleUpdateFeedTypeUtilisation = useCallback(
    (targetAnimalGroupId: string, feedTypeId: string, percent: number) => {
      updatePlan((p) => ({
        ...p,
        animalGroupBudgets: p.animalGroupBudgets.map((agb) =>
          agb.animalGroupId === targetAnimalGroupId
            ? {
                ...agb,
                feedTypeUtilisation: {
                  ...(agb.feedTypeUtilisation ?? {}),
                  [feedTypeId]: percent,
                },
              }
            : agb
        ),
      }));
    },
    [updatePlan]
  );

  const handleUpdateGrazingUtilisation = useCallback(
    (targetAnimalGroupId: string, percent: number) => {
      updatePlan((p) => ({
        ...p,
        animalGroupBudgets: p.animalGroupBudgets.map((agb) =>
          agb.animalGroupId === targetAnimalGroupId
            ? { ...agb, grazingUtilisationPercent: percent }
            : agb
        ),
      }));
    },
    [updatePlan]
  );

  const handleUpdateFieldFeedingUtilisation = useCallback(
    (targetAnimalGroupId: string, percent: number) => {
      updatePlan((p) => ({
        ...p,
        animalGroupBudgets: p.animalGroupBudgets.map((agb) =>
          agb.animalGroupId === targetAnimalGroupId
            ? { ...agb, fieldFeedingUtilisationPercent: percent }
            : agb
        ),
      }));
    },
    [updatePlan]
  );

  const handleUpdateHousing = useCallback(
    (
      targetAnimalGroupId: string,
      stockClassId: string,
      month: number,
      yearOffset: 0 | 1,
      isGrazing: boolean
    ) => {
      updatePlan((p) => ({
        ...p,
        animalGroupBudgets: p.animalGroupBudgets.map((agb) =>
          agb.animalGroupId === targetAnimalGroupId
            ? (() => {
                const nextStockClassPlans = agb.stockClassPlans.map((sc) =>
                  sc.stockClassId === stockClassId
                    ? {
                        ...sc,
                        months: sc.months.map((m) =>
                          m.month === month &&
                          (m.yearOffset ?? 0) === yearOffset
                            ? { ...m, isGrazing }
                            : m
                        ),
                      }
                    : sc
                );
                const configuredUtilisation =
                  agb.grazingUtilisationPercent ??
                  p.grazingMonths.find(
                    (gm) =>
                      gm.month === month && (gm.yearOffset ?? 0) === yearOffset
                  )?.utilisationPercent ??
                  p.grazingMonths.find((gm) => gm.month === month)
                    ?.utilisationPercent ??
                  0;
                const configuredGrowthRate =
                  p.grazingMonths.find(
                    (gm) =>
                      gm.month === month && (gm.yearOffset ?? 0) === yearOffset
                  )?.growthRateKgHaDay ??
                  p.grazingMonths.find((gm) => gm.month === month)
                    ?.growthRateKgHaDay ??
                  0;
                const supplyKgHaDay =
                  (configuredGrowthRate * configuredUtilisation) / 100;
                const totalDemandKgDay = nextStockClassPlans.reduce(
                  (sum, sc) => {
                    const entry = sc.months.find(
                      (m) =>
                        m.month === month && (m.yearOffset ?? 0) === yearOffset
                    );
                    if (!entry) return sum;
                    return (
                      sum +
                      ((entry.averageWeightKg * entry.allocationPercent) /
                        100) *
                        entry.animalCount
                    );
                  },
                  0
                );
                const requiredAreaHa =
                  isGrazing && supplyKgHaDay > 0
                    ? Math.max(0, totalDemandKgDay / supplyKgHaDay)
                    : null;
                return {
                  ...agb,
                  stockClassPlans: nextStockClassPlans,
                  monthlyGrazingAreaHa:
                    requiredAreaHa === null
                      ? agb.monthlyGrazingAreaHa
                      : {
                          ...(agb.monthlyGrazingAreaHa ?? {}),
                          [month]: requiredAreaHa,
                        },
                };
              })()
            : agb
        ),
      }));
    },
    [updatePlan]
  );

  const handleUpdateMonthlyArea = useCallback(
    (targetAnimalGroupId: string, month: number, areaHa: number) => {
      updatePlan((p) => ({
        ...p,
        animalGroupBudgets: p.animalGroupBudgets.map((agb) =>
          agb.animalGroupId === targetAnimalGroupId
            ? {
                ...agb,
                monthlyGrazingAreaHa: {
                  ...(agb.monthlyGrazingAreaHa ?? {}),
                  [month]: Math.max(0, areaHa),
                },
              }
            : agb
        ),
      }));
    },
    [updatePlan]
  );

  const handleUpdateGrowthRate = useCallback(
    (month: number, yearOffset: 0 | 1, value: number) => {
      updatePlan((p) => ({
        ...p,
        grazingMonths: upsertGrazingMonthGrowthRate(
          p.grazingMonths,
          month,
          yearOffset,
          value
        ),
      }));
    },
    [updatePlan]
  );

  const handleRenameStockClass = useCallback(
    (targetAnimalGroupId: string, stockClassId: string, name: string) => {
      updatePlan((p) => ({
        ...p,
        animalGroupBudgets: p.animalGroupBudgets.map((agb) =>
          agb.animalGroupId === targetAnimalGroupId
            ? {
                ...agb,
                stockClassPlans: agb.stockClassPlans.map((sc) =>
                  sc.stockClassId === stockClassId
                    ? { ...sc, stockClassName: name }
                    : sc
                ),
              }
            : agb
        ),
      }));
    },
    [updatePlan]
  );

  const handleExtendPlan = useCallback(
    (newEndDate: string) => {
      if (plan) updatePlanMetadata(plan.guid, { endDate: newEndDate });
    },
    [plan, updatePlanMetadata]
  );

  return (
    <>
      {isMobile ? (
        <BottomSheetHeader
          titlePrefix={`${t("animalGroup.prefix")}:`}
          title={name}
          onBack={handleBackToFarm}
          rightAction={<AnimalGroupMenu {...animalGroup} />}
        />
      ) : (
        <PageTitleBar
          titlePrefix={`${t("animalGroup.prefix")}:`}
          title={name}
          onBack={handleBackToFarm}
        >
          <AnimalGroupMenu {...animalGroup} />
        </PageTitleBar>
      )}

      {/* Animal Group Color Accent Strip */}
      {animalGroupColor && (
        <Box
          sx={{
            height: 4,
            backgroundColor: animalGroupColor,
            width: "100%",
            mb: 2,
          }}
        />
      )}

      <Container
        maxWidth="sm"
        sx={{
          pb: isMobile ? 10 : 2,
          pt: animalGroupColor ? 0 : 2,
        }}
      >
        {/* Current Field Warning Banner */}
        {fieldCount > 0 && !currentField && (
          <Box
            sx={{
              backgroundColor: "#FDFBEB",
              border: "1px solid #F7E880",
              borderRadius: 2,
              px: 1.5,
              py: 1,
              mb: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: "#A48137", fontSize: "0.8rem" }}
            >
              {t("animalGroup.currentFieldNotSet")}
            </Typography>
            <Button
              size="small"
              onClick={() => setSetCurrentFieldDialogOpen(true)}
              sx={{
                color: "#A48137",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.75rem",
                padding: "4px 12px",
                minWidth: "auto",
                backgroundColor: "rgba(164, 129, 55, 0.1)",
                "&:hover": {
                  backgroundColor: "rgba(164, 129, 55, 0.2)",
                },
              }}
            >
              {t("animalGroup.setCurrentField")}
            </Button>
          </Box>
        )}

        {/* Animal Group Metrics */}
        <Box sx={{ mb: 2 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 1,
            }}
          >
            {/* First card: Animals (with LU), Assigned Area, Stocking Rate */}
            <MetricsCard>
              <MetricItem
                label={t("animalGroup.animalCount")}
                value={animalCount || 0}
                secondaryValue={`${livestockUnits.toFixed(1)} LU`}
                infoTooltip={t("animalGroup.metrics.animalCount.info")}
              />
              {fieldCount > 0 ? (
                <MetricItem
                  label={t("animalGroup.metrics.assignedArea")}
                  value={totalAreaRounded}
                  unit="Ha"
                  infoTooltip={t("animalGroup.metrics.assignedArea.info")}
                />
              ) : (
                <WarningItem
                  label={t("animalGroup.metrics.assignedArea")}
                  value={t("animalGroup.metrics.none")}
                />
              )}
              <MetricItem
                label={t("animalGroup.metrics.stockingRate")}
                value={stockingRate}
                unit={stockingRate !== "-" ? "LU/Ha" : undefined}
                infoTooltip={t("animalGroup.metrics.stockingRate.info")}
              />
            </MetricsCard>

            {/* Second card: Forage Available, Daily Feed Demand, Grazing Days */}
            <MetricsCard>
              <MetricItem
                label={t("animalGroup.metrics.forageAvailable")}
                value={dryMatterTonnes}
                unit="t DM"
                infoTooltip={t("animalGroup.metrics.forageAvailable.info")}
              />
              <MetricItem
                label={t("animalGroup.metrics.dailyFeedDemand")}
                value={
                  totalDailyDemand > 0 ? Math.round(totalDailyDemand) : "-"
                }
                unit={totalDailyDemand > 0 ? "kgDM/day" : undefined}
                infoTooltip={t("animalGroup.metrics.dailyFeedDemand.info")}
              />
              <MetricItem
                label={t("animalGroup.metrics.grazingDaysAhead")}
                value={grazingDaysAhead > 0 ? grazingDaysAhead : "-"}
                unit={grazingDaysAhead > 0 ? "days" : undefined}
                infoTooltip={t("animalGroup.metrics.grazingDaysAhead.info")}
              />
            </MetricsCard>
          </Box>

          {/* Stock Classes — derived from feed budget plan when available */}
          {(() => {
            const scPlans = selectedBudget?.stockClassPlans;
            const month = new Date().getMonth();
            const displayClasses =
              scPlans && scPlans.length > 0
                ? scPlans.map((sc) => ({
                    id: sc.stockClassId,
                    name: sc.stockClassName,
                    count: sc.months[month]?.animalCount ?? 0,
                  }))
                : stockClasses.map((sc) => ({
                    id: sc.guid,
                    name: sc.name,
                    count: sc.animalCount,
                  }));
            return displayClasses.length > 0 ? (
              <Box sx={{ mt: 1.5 }}>
                <MetricsCard>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      display: "block",
                      fontSize: "0.7rem",
                      pt: 0.5,
                      pb: 0.25,
                    }}
                  >
                    {t("animalGroup.metrics.stockClasses")}
                  </Typography>
                  {displayClasses.map((sc) => (
                    <StockClassRow
                      key={sc.id}
                      name={sc.name}
                      count={sc.count}
                    />
                  ))}
                </MetricsCard>
              </Box>
            ) : null;
          })()}
        </Box>

        {/* Planning Tools Preview Cards */}
        <Box sx={{ mb: 2 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              gap: 1,
            }}
          >
            <Box>
              <GrassWedgePreviewCard
                animalGroupId={animalGroupId}
                disabled={!showGrassWedgeButton}
              />
            </Box>
            <Box>
              <FeedBudgetPreviewCard onClick={handleOpenFeedBudgetDialog} />
            </Box>
            <Box>
              <RotationPreviewCard animalGroupId={animalGroupId} />
            </Box>
            <Box>
              <PlanningActionsCard
                onMoveToShed={handleMoveToShed}
                onAddFeedEvent={() => setFeedEventDialogOpen(true)}
                moveToShedDisabled={!currentField}
              />
            </Box>
          </Box>
          {showGrassWedgeButton && (
            <GrassWedgeDialog
              open={Boolean(grassWedgeMatch)}
              close={() => navigate(`/animal-group/${animalGroupId}`)}
              animalGroup={animalGroup}
            />
          )}
          <RotationTimelineDialog
            open={Boolean(rotationTimelineMatch)}
            close={() => navigate(`/animal-group/${animalGroupId}`)}
            animalGroupId={animalGroupId}
            animalGroupName={animalGroup?.name ?? ""}
            animalGroup={animalGroup}
            stockClasses={stockClasses}
          />
          {selectedBudget && feedBudgetComputed && plan && (
            <AnimalGroupDetailDialog
              open={feedBudgetDialogOpen}
              onClose={() => setFeedBudgetDialogOpen(false)}
              budget={selectedBudget}
              grazingMonths={plan.grazingMonths}
              groupRows={
                feedBudgetComputed.animalGroupRows.get(
                  selectedBudget.animalGroupId
                ) ?? []
              }
              groupRowsYear1={feedBudgetComputed.animalGroupRowsYear1.get(
                selectedBudget.animalGroupId
              )}
              stockClassComputed={feedBudgetComputed.stockClassComputed}
              stockClassComputedYear1={
                feedBudgetComputed.stockClassComputedYear1
              }
              actualGrowthRatesKgHaDay={actualFeedData.actualGrowthRatesKgHaDay}
              currentMonth={actualFeedData.currentMonth}
              planStartDate={plan.startDate}
              planEndDate={plan.endDate}
              onExtendPlan={handleExtendPlan}
              onUpdateStockClass={handleUpdateStockClass}
              onUpdateSupplementAmount={handleUpdateSupplementAmount}
              onUpdateSupplementUtilisation={handleUpdateSupplementUtilisation}
              onUpdateFeedTypeUtilisation={handleUpdateFeedTypeUtilisation}
              onUpdateGrazingUtilisation={handleUpdateGrazingUtilisation}
              onUpdateFieldFeedingUtilisation={
                handleUpdateFieldFeedingUtilisation
              }
              onUpdateHousing={handleUpdateHousing}
              onUpdateMonthlyArea={handleUpdateMonthlyArea}
              onUpdateGrowthRate={handleUpdateGrowthRate}
              onAddSupplementFeedType={addSupplementFeedType}
              onRemoveSupplementFeedType={removeSupplementFeedType}
              onAddStockClass={(agId, name, defaultScName, values) =>
                addStockClassToGroup(agId, name, defaultScName, values)
              }
              onRemoveStockClass={removeStockClassFromGroup}
              onRenameStockClass={handleRenameStockClass}
              isDirty={isDirty}
              isSaving={isSaving}
              onSave={savePlan}
              onDiscard={() =>
                discardGroupChanges(selectedBudget.animalGroupId)
              }
            />
          )}
        </Box>

        {/* Assigned Fields Section */}
        <Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                fontWeight: 600,
              }}
            >
              {t("animalGroup.metrics.assignedFields")}
            </Typography>
            <Button
              startIcon={<AddCircleIcon sx={{ fontSize: "16px !important" }} />}
              size="small"
              onClick={() => {
                analytics.track(
                  AnalyticsEvents.ANIMAL_GROUP_ASSIGN_FIELDS_CLICKED
                );
                setAssignFieldDialogOpen(true);
              }}
              sx={{
                color: "primary.main",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.8rem",
                padding: "4px 8px",
                minWidth: "auto",
                "&:hover": {
                  backgroundColor: "rgba(56, 142, 60, 0.08)",
                },
              }}
            >
              {t("animalGroup.assignField")}
            </Button>
          </Box>
          <AssignFieldDialog
            open={assignFieldDialogOpen}
            close={() => setAssignFieldDialogOpen(false)}
            animalGroupId={animalGroupId}
          />
          <SetCurrentFieldDialog
            open={setCurrentFieldDialogOpen}
            close={() => setSetCurrentFieldDialogOpen(false)}
            animalGroupId={animalGroupId}
          />
          <AddFeedEventDialog
            dialogOpen={feedEventDialogOpen}
            setDialogOpen={setFeedEventDialogOpen}
          />
          <Box
            sx={{
              backgroundColor: "white",
              borderRadius: 2,
              border: `1px solid ${BELLFLOWER_COLOR}`,
              overflow: "hidden",
            }}
          >
            <FieldsList fieldIds={fields.map((field) => field.guid)} />
          </Box>
        </Box>
      </Container>
    </>
  );
}

function useZoomToFieldGeoms(animalGroupId: string) {
  const setZoomToGeom = useUIStore((state) => state.setZoomToGeom);
  // Use React Query for fields data
  const { data: allFields = [] } = useFields();
  const fieldGeoms = useMemo(
    () => getFieldGeomsForAnimalGroupFromArray(allFields, animalGroupId),
    [allFields, animalGroupId]
  );
  // Track which animal group we've zoomed to, so we only zoom once per animal group
  const zoomedToAnimalGroupRef = useRef<string | null>(null);

  useEffect(() => {
    // Only zoom if we haven't already zoomed to this animal group
    if (zoomedToAnimalGroupRef.current === animalGroupId) {
      return;
    }

    if (fieldGeoms.features.length > 0) {
      zoomedToAnimalGroupRef.current = animalGroupId;
      setZoomToGeom(fieldGeoms);
    }
  }, [animalGroupId, fieldGeoms, setZoomToGeom]);
}

function GrassWedgePreviewCard({
  animalGroupId,
  disabled,
}: GrassWedgeCardProps) {
  const { t } = useTranslation();

  // Bar heights and colors for the preview chart (descending order - tallest first)
  // Colors go from dark (tall grass) to light (short grass)
  const barHeights = [85, 80, 75, 70, 65, 60, 45];
  const barColors = [
    "#2E7D32",
    "#388E3C",
    "#43A047",
    "#4CAF50",
    "#66BB6A",
    "#81C784",
    "#A5D6A7",
  ];

  const cardContent = (
    <Box
      sx={{
        backgroundColor: "white",
        borderRadius: "12px",
        boxShadow: "0 1px 4px rgba(0, 0, 0, 0.08)",
        overflow: "hidden",
        height: PLANNING_CARD_HEIGHT,
        display: "flex",
        flexDirection: "column",
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.6 : 1,
        transition: "transform 0.2s, box-shadow 0.2s",
        "&:hover": disabled
          ? {}
          : {
              transform: "translateY(-2px)",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.12)",
            },
      }}
    >
      {/* Chart Preview Area */}
      <Box
        sx={{
          p: 1,
          pb: 0.75,
          position: "relative",
          flex: 1,
        }}
      >
        <Box
          sx={{ display: "flex", alignItems: "flex-end", gap: 0.4, height: 52 }}
        >
          {/* Y-axis placeholder lines */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              height: "100%",
              pr: 1,
            }}
          >
            {[null, null, null].map((_, i) => (
              <Box
                key={i}
                sx={{
                  width: 20,
                  height: 2,
                  backgroundColor: "#E0E0E0",
                  borderRadius: 1,
                }}
              />
            ))}
          </Box>

          {/* Bars */}
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-end",
              gap: 0.75,
              flex: 1,
              height: "100%",
              position: "relative",
            }}
          >
            {barHeights.map((height, index) => (
              <Box
                key={index}
                sx={{
                  flex: 1,
                  height: `${height}%`,
                  backgroundColor: barColors[index],
                  borderRadius: "4px 4px 0 0",
                  minWidth: 10,
                }}
              />
            ))}

            {/* Trend line overlay */}
            <Box
              sx={{
                position: "absolute",
                left: 0,
                right: 0,
                top: 0,
                bottom: 0,
                pointerEvents: "none",
              }}
            >
              <svg
                width="100%"
                height="100%"
                viewBox="0 0 100 100"
                preserveAspectRatio="none"
                style={{ overflow: "visible" }}
              >
                <line
                  x1="0"
                  y1="20"
                  x2="100"
                  y2="60"
                  stroke="#7D5DA3"
                  strokeWidth="4"
                  strokeLinecap="round"
                  vectorEffect="non-scaling-stroke"
                />
              </svg>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Footer with label */}
      <Box
        sx={{
          px: 1,
          py: 0.75,
          borderTop: "1px solid #F0F0F0",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color: "#1C4321",
            fontSize: "0.74rem",
          }}
        >
          {t("grassWedge.open")}
        </Typography>
      </Box>
    </Box>
  );

  if (disabled) {
    return (
      <Tooltip title={t("animalGroup.grassWedgeButton.tooltip")} arrow>
        <Box>{cardContent}</Box>
      </Tooltip>
    );
  }

  return (
    <Link
      to={`/animal-group/${animalGroupId}/grass-wedge`}
      style={{ textDecoration: "none" }}
    >
      {cardContent}
    </Link>
  );
}

type GrassWedgeCardProps = {
  animalGroupId: string;
  disabled?: boolean;
};

function FeedBudgetPreviewCard({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation();
  const monthlyBars = [
    { month: "Jan", height: 18, surplus: false },
    { month: "Feb", height: 6, surplus: false },
    { month: "Mar", height: 14, surplus: false },
    { month: "Apr", height: 28, surplus: true },
    { month: "May", height: 40, surplus: true },
    { month: "Jun", height: 29, surplus: true },
    { month: "Jul", height: 28, surplus: true },
    { month: "Aug", height: 21, surplus: true },
    { month: "Sep", height: 15, surplus: true },
    { month: "Oct", height: 8, surplus: true },
    { month: "Nov", height: 6, surplus: false },
    { month: "Dec", height: 18, surplus: false },
  ];
  const surplusColor = "#A8D97A";
  const deficitColor = "#F0A3A6";

  return (
    <Box
      component="button"
      onClick={() => {
        analytics.track(AnalyticsEvents.FEED_BUDGET_VIEW_OPENED);
        onClick();
      }}
      sx={{
        border: "none",
        display: "block",
        width: "100%",
        textDecoration: "none",
        backgroundColor: "white",
        borderRadius: "12px",
        boxShadow: "0 1px 4px rgba(0, 0, 0, 0.08)",
        overflow: "hidden",
        height: PLANNING_CARD_HEIGHT,
        display: "flex",
        flexDirection: "column",
        cursor: "pointer",
        transition: "transform 0.2s, box-shadow 0.2s",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.12)",
        },
      }}
    >
      <Box sx={{ p: 1, pb: 0.75, flex: 1 }}>
        <Box
          sx={{
            height: 44,
            display: "flex",
            alignItems: "flex-end",
            gap: 0.35,
          }}
        >
          {monthlyBars.map((bar) => (
            <Box
              key={bar.month}
              sx={{
                flex: 1,
                height: bar.height,
                borderRadius: "4px 4px 0 0",
                backgroundColor: bar.surplus ? surplusColor : deficitColor,
              }}
            />
          ))}
        </Box>
      </Box>
      <Box
        sx={{
          px: 1,
          py: 0.75,
          borderTop: "1px solid #F0F0F0",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, color: "#2E5C2E", fontSize: "0.74rem" }}
        >
          {t("animalGroup.viewFeedAllocation")}
        </Typography>
      </Box>
    </Box>
  );
}

function RotationPreviewCard({ animalGroupId }: { animalGroupId: string }) {
  const { t } = useTranslation();
  const rotationColumns = [
    [
      { width: "88%", color: "#A7D882" },
      { width: "74%", color: "#A7D882" },
      { width: "62%", color: "#A7D882" },
    ],
    [
      { width: "82%", color: "#A7D882" },
      { width: "68%", color: "#C9BEDD" },
      { width: "58%", color: "#A7D882" },
    ],
    [{ width: "76%", color: "#C9BEDD" }],
  ];

  return (
    <Box
      component={Link}
      to={`/animal-group/${animalGroupId}/rotations`}
      sx={{
        display: "block",
        textDecoration: "none",
        backgroundColor: "white",
        borderRadius: "12px",
        boxShadow: "0 1px 4px rgba(0, 0, 0, 0.08)",
        overflow: "hidden",
        height: PLANNING_CARD_HEIGHT,
        display: "flex",
        flexDirection: "column",
        transition: "transform 0.2s, box-shadow 0.2s",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.12)",
        },
      }}
    >
      <Box sx={{ p: 1, pb: 0.75, flex: 1 }}>
        <Box
          sx={{
            height: 44,
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 0.5,
            alignItems: "stretch",
            borderRadius: 1,
            backgroundColor: "#FFFFFF",
            p: 0.25,
          }}
        >
          {rotationColumns.map((column, idx) => (
            <Box
              key={idx}
              sx={{
                borderRadius: 1,
                backgroundColor: "#F8F8FA",
                px: 0.4,
                py: 0.32,
                display: "flex",
                flexDirection: "column",
                gap: 0.26,
              }}
            >
              {column.map((event, eventIdx) => (
                <Box
                  key={eventIdx}
                  sx={{
                    height: 7,
                    width: event.width,
                    borderRadius: 999,
                    backgroundColor: event.color,
                    border: "1px solid rgba(90,90,90,0.12)",
                  }}
                />
              ))}
            </Box>
          ))}
        </Box>
      </Box>
      <Box
        sx={{
          px: 1,
          py: 0.75,
          borderTop: "1px solid #F0F0F0",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, color: "#2E5C2E", fontSize: "0.74rem" }}
        >
          {t("animalGroup.viewRotations")}
        </Typography>
      </Box>
    </Box>
  );
}

function PlanningActionsCard({
  onMoveToShed,
  onAddFeedEvent,
  moveToShedDisabled,
}: {
  onMoveToShed: () => void;
  onAddFeedEvent: () => void;
  moveToShedDisabled: boolean;
}) {
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        backgroundColor: "white",
        borderRadius: "12px",
        boxShadow: "0 1px 4px rgba(0, 0, 0, 0.08)",
        height: PLANNING_CARD_HEIGHT,
        p: 1,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        gap: 0.75,
      }}
    >
      <Button
        variant="outlined"
        size="small"
        startIcon={<HomeIcon sx={{ fontSize: "0.95rem !important" }} />}
        disabled={moveToShedDisabled}
        onClick={onMoveToShed}
        sx={{
          textTransform: "none",
          fontWeight: 600,
          fontSize: "0.74rem",
          py: 0.45,
          borderColor: "#7D5DA3",
          color: "#7D5DA3",
          "&:hover": {
            borderColor: "#5E3F87",
            backgroundColor: "rgba(125, 93, 163, 0.08)",
          },
        }}
      >
        {t("animalGroup.moveToShed")}
      </Button>
      <Button
        variant="outlined"
        size="small"
        onClick={onAddFeedEvent}
        sx={{
          textTransform: "none",
          fontWeight: 600,
          fontSize: "0.74rem",
          py: 0.45,
          borderColor: "#2E5C2E",
          color: "#2E5C2E",
          "&:hover": {
            borderColor: "#1F4620",
            backgroundColor: "rgba(46, 92, 46, 0.08)",
          },
        }}
      >
        {t("feedEventDetailView.button.addFeedEvent.title")}
      </Button>
    </Box>
  );
}
